/* Zugangsanfragen neuer Nutzer.
 *
 * Beim Zugriffstest am 2026-08-26 war diese Tabelle fuer JEDEN eingeloggten
 * Nutzer lesbar, obwohl sie Namen und E-Mail-Adressen enthaelt und ein reiner
 * Adminbereich ist. Abgestellt wird das in supabase/profiles.sql, Abschnitt 7
 * — solange das Skript nicht eingespielt ist, ist die Einschraenkung hier nur
 * Oberflaechensteuerung.
 *
 * Eintragen duerfen auch nicht angemeldete Besucher: Das Formular auf der
 * Anmeldeseite laeuft ohne Sitzung. Nur Lesen und Entscheiden ist Admins
 * vorbehalten.
 */

import { supabase } from './client.js';

/**
 * Neue Anfrage eintragen — auch ohne Anmeldung (anon). Erwartet geprüfte Werte aus
 * normalizeAccessRequest (domain/accessRequest.js). `name` bleibt die Spalte für den
 * Vornamen (Altzeilen: ganzer Name). Die Datenbank prüft Pflichtfelder und Längen
 * noch einmal (Regel access_requests_insert_public); ein Trigger meldet die Anfrage
 * den Admins — scheitert der, wird die Anfrage trotzdem gespeichert.
 */
export async function submitAccessRequest({ firstName, lastName, organization, email }) {
  if (!supabase) throw new Error('notConnected');
  const { error } = await supabase.from('access_requests').insert({
    name: firstName,
    last_name: lastName,
    organization,
    email,
    status: 'pending',
  });
  if (error) {
    console.error('[requests] Zugangsanfrage fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }
}

/* Offen = noch nicht eingeladen: pending (neu) und approved (Altfälle, früher nur
   "angenommen", nie eingeladen). Danach folgt invited bzw. rejected. */
export async function loadOpenRequests() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('access_requests')
    .select('*')
    .in('status', ['pending', 'approved'])
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[requests] Laden fehlgeschlagen:', error.message);
    throw new Error('loadFailed');
  }
  return data || [];
}

/** Ablehnen. Annehmen läuft über inviteRequest — das legt das Konto an. */
export async function decideRequest(id, decision) {
  if (!supabase) throw new Error('notConnected');
  const { error } = await supabase
    .from('access_requests')
    .update({ status: decision })
    .eq('id', id);

  if (error) {
    console.error('[requests] Entscheidung fehlgeschlagen:', error.message);
    throw new Error('saveFailed');
  }
}

/* Fehlercodes der Edge Function invite-user, die die App kennt. Alles andere gilt als
   inviteFailed — Meldungen des Servers erscheinen nie im UI. */
const INVITE_ERRORS = [
  'forbidden', 'notAuthenticated', 'alreadyRegistered', 'notInvitable',
  'roleNotSet', 'emailRateLimit', 'inviteFailed',
];

/**
 * Anfrage per Supabase-Mail einladen und Rolle setzen (Edge Function invite-user).
 * Adresse und Name nimmt die Function selbst aus der Anfrage — der Client schickt nur
 * Anfrage-ID und Rolle. Wirft Error(code) mit einem Code aus INVITE_ERRORS.
 */
export async function inviteRequest(id, role) {
  if (!supabase) throw new Error('notConnected');
  const { error } = await supabase.functions.invoke('invite-user', {
    body: { request_id: id, role },
  });
  if (!error) return;

  let code = 'inviteFailed';
  try {
    const body = await error.context.json();
    if (INVITE_ERRORS.includes(body?.error)) code = body.error;
  } catch {
    /* kein JSON (Function nicht erreichbar / nicht deployt) — bleibt inviteFailed */
  }
  console.error('[requests] Einladung fehlgeschlagen:', code);
  throw new Error(code);
}

/**
 * Live-Aktualisierung, damit der Hinweis in der Kopfleiste ohne Neuladen
 * erscheint. Setzt voraus, dass fuer die Tabelle in Supabase "Realtime
 * replication" eingeschaltet ist — ist sie es nicht, passiert schlicht
 * nichts und die Liste aktualisiert sich beim naechsten Oeffnen.
 */
export function subscribeRequests(onChange) {
  if (!supabase || !supabase.channel) return null;
  try {
    return supabase
      .channel('access-requests-stream')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'access_requests' },
        (payload) => onChange(payload)
      )
      .subscribe();
  } catch {
    return null;
  }
}

export function unsubscribeRequests(channel) {
  if (channel && supabase) {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* egal — die Verbindung endet ohnehin mit der Seite */
    }
  }
}
